import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import DataManagement from './pages/DataManagement';
import SearchRecords from './pages/SearchRecords';
import ActivityLogs from './pages/ActivityLogs';
import AgentDashboard from './pages/AgentDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import UserManagement from './pages/UserManagement';
import IPManagement from './pages/IPManagement';
import AccessDenied from './pages/AccessDenied';
import Chat from './pages/Chat';
import AdminChatMonitor from './pages/AdminChatMonitor';
import Leaderboard from './pages/Leaderboard';
import AdminNotifications from './pages/AdminNotifications';
import AttendanceDashboard from './pages/AttendanceDashboard';
import PasswordChangeLogs from './pages/PasswordChangeLogs';
import InvoiceGenerator from './pages/InvoiceGenerator';
import TodaySales from './pages/TodaySales';
import TVDisplay from './pages/TVDisplay';
import BiometricSettings from './pages/BiometricSettings';
import NewJoining from './pages/NewJoining';
import HolidayManagement from './pages/HolidayManagement';
import Payroll from './pages/Payroll';
import MyPayslips from './pages/MyPayslips';
import NotificationPopup from './components/NotificationPopup';
import { Toaster } from './components/ui/sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ipAllowed, setIpAllowed] = useState(null);
  const [clientIP, setClientIP] = useState('');

  useEffect(() => {
    // Generic: forward unrecognized /admin/ paths to backend API
    const p = window.location.pathname;
    if (p.startsWith('/admin/') && !['/admin'].includes(p)) {
      window.location.replace('/api' + p);
      return;
    }
    
    // Check for access token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token');
    
    if (accessToken) {
      localStorage.setItem('admin_access_token', accessToken);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    checkIPAccess();
  }, []);

  const checkIPAccess = async () => {
    try {
      const accessToken = localStorage.getItem('admin_access_token');
      
      let url = accessToken 
        ? `${BACKEND_URL}/api/ip/check-with-token?access_token=${accessToken}`
        : `${BACKEND_URL}/api/ip/check`;
      
      const response = await axios.get(url, { withCredentials: true });
      setIpAllowed(response.data.is_allowed);
      setClientIP(response.data.ip_address);
      
      // Only load user data if IP is allowed
      if (response.data.is_allowed) {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        if (token && userData) {
          setUser(JSON.parse(userData));
        }
      }
    } catch (error) {
      console.error('IP check failed:', error);
      // If IP check fails, allow access (fail-open for development)
      setIpAllowed(true);
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (token && userData) {
        setUser(JSON.parse(userData));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Request notification permission on login (Sidebar handles the actual badge polling)
  useEffect(() => {
    if (user && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Verifying access...</p>
        </div>
      </div>
    );
  }

  // TV Display - IP restricted but no login required
  if (window.location.pathname === '/tv-display') {
    if (ipAllowed === false) {
      return <AccessDenied ipAddress={clientIP} />;
    }
    return <TVDisplay />;
  }

  // Show access denied page if IP is not whitelisted
  if (ipAllowed === false) {
    return <AccessDenied ipAddress={clientIP} />;
  }

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
          />
          <Route
            path="/"
            element={
              user ? (
                user.role === 'admin' ? (
                  <AdminDashboard user={user} onLogout={handleLogout} />
                ) : user.role === 'manager' ? (
                  <ManagerDashboard user={user} onLogout={handleLogout} />
                ) : (
                  <AgentDashboard user={user} onLogout={handleLogout} />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/data"
            element={user && (user.role === 'admin' || user.role === 'manager') ? <DataManagement user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/search"
            element={user ? <SearchRecords user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/activity"
            element={user && user.role === 'admin' ? <ActivityLogs user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/users"
            element={user && user.role === 'admin' ? <UserManagement user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/ip-management"
            element={user && user.role === 'admin' ? <IPManagement user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/chat"
            element={user ? <Chat user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/chat-monitor"
            element={user && user.role === 'admin' ? <AdminChatMonitor user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/leaderboard"
            element={user ? <Leaderboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/broadcast"
            element={user && user.role === 'admin' ? <AdminNotifications user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/password-logs"
            element={user && user.role === 'admin' ? <PasswordChangeLogs user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/invoice"
            element={user && user.role === 'admin' ? <InvoiceGenerator user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/attendance"
            element={user ? <AttendanceDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/today-sales"
            element={user ? <TodaySales user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/biometric-settings"
            element={user && user.role === 'admin' ? <BiometricSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/new-joining"
            element={user && user.role === 'admin' ? <NewJoining user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/holidays"
            element={user && (user.role === 'admin' || user.role === 'manager') ? <HolidayManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/payroll"
            element={user && user.role === 'admin' ? <Payroll user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/my-payslips"
            element={user ? <MyPayslips user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
        </Routes>
        {/* Global Notification Popup for all users */}
        {user && <NotificationPopup user={user} />}
      </BrowserRouter>
      <Toaster />
    </>
  );
}

export default App;
